import { Extension } from "@tiptap/core";

/**
 * What a block holds that the editor does not model, kept across an edit.
 *
 * The Flow editor saves a whole block from the fields its serializer knows,
 * and the server stores exactly that — so anything else was dropped on every
 * save. Measured 26 Sep (open a document, type in one paragraph, save): a
 * heading lost its label, an equation its numbered:false (the star, undoing
 * #145) and displayMode, a code block its caption, label and lineNumbers, a
 * theorem its numbered, a table hasHeader / longTable / span, a table of
 * contents its depth.
 *
 * `carry` is the block's content as loaded, invisible (never rendered, so it
 * does not travel through the clipboard) and not copied when a block splits.
 * On save the serializer's fields are laid over it, so every field the editor
 * models always takes its current value and only the rest comes from here.
 * Figures keep their own `extra` (#326), the same idea done first.
 */
export const CARRY_NODE_TYPES = [
  "paragraph", "heading", "equationBlock", "codeBlockNode", "tableBlock", "theoremBlock",
  "abstractBlock", "algorithmBlock", "bibliographyBlock", "blockquoteBlock", "calloutBlock",
  "columnBreakBlock", "cvEntryBlock", "cvSectionBlock", "embedBlock", "footnoteBlockNode",
  "listBlock", "pageBreakBlock", "personalInfoBlock", "photoBlock", "titleBlock", "tocBlock",
  "verseBlock", "asideBlock", "annotationBlock", "coverBlock", "chapterBreakBlock", "frontMatterBlock",
  "backMatterBlock",
];

export interface BlockCarry {
  /** The block type the content belongs to — merged back only while it still is. */
  type: string;
  content: Record<string, unknown>;
}

/**
 * Fields never carried, because the server derives them from a field the
 * editor does write: an equation's `source` (and its `ast`, `notation`) is
 * rebuilt from `latex` — but the normaliser prefers `source`, so a stale one
 * carried along would silently override the author's edit.
 */
const DERIVED: Record<string, readonly string[]> = {
  equation: ["source", "ast", "notation"],
};

export function carryOf(type: string, content: unknown): BlockCarry | null {
  if (!content || typeof content !== "object" || Array.isArray(content)) return null;
  const skip = new Set(DERIVED[type] ?? []);
  const kept = Object.fromEntries(Object.entries(content as Record<string, unknown>).filter(([k]) => !skip.has(k)));
  return Object.keys(kept).length ? { type, content: kept } : null;
}

/** The saved content: what was carried, under what the editor wrote. */
export function withCarry(type: string, content: Record<string, unknown>, carry: unknown): Record<string, unknown> {
  const c = carry as BlockCarry | null | undefined;
  if (!c || typeof c !== "object" || c.type !== type || !c.content) return content;
  return { ...c.content, ...content };
}

export const BlockCarryExtension = Extension.create({
  name: "blockCarry",
  addGlobalAttributes() {
    return [
      {
        types: CARRY_NODE_TYPES,
        attributes: {
          carry: { default: null, rendered: false, keepOnSplit: false },
        },
      },
    ];
  },
});
